import { useState } from "react";
import { Link, Outlet, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { HardHat, LayoutGrid, LogOut, Menu, X, Building2, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials, PROJECT_STATUS_COLOR } from "@/lib/constants";
import { Button } from "@/components/ui/button";

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { id: activeId } = useParams();

  const { data } = useQuery({
    queryKey: ["projects-nav"],
    queryFn: async () => (await api.get("/projects")).data.projects,
  });
  const projects = data || [];

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--sidebar))]">
      <div className="p-5 border-b border-border">
        <Link to="/app" onClick={onNavigate} className="flex items-center gap-3" data-testid="sidebar-logo">
          <div className="h-10 w-10 grid place-items-center bg-primary rounded-md shrink-0">
            <HardHat className="h-6 w-6 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <div className="font-display font-black tracking-tight leading-none">
            CONSTRUCTVISION<span className="text-primary">-3D</span>
          </div>
        </Link>
      </div>

      <div className="p-3">
        <Link to="/app" onClick={onNavigate}
          data-testid="nav-all-projects"
          className={`flex items-center gap-3 px-3 h-12 rounded-md font-semibold transition-colors ${
            location.pathname === "/app" ? "bg-primary text-primary-foreground" : "hover:bg-card text-foreground"
          }`}>
          <LayoutGrid className="h-5 w-5" />
          All Projects
        </Link>
      </div>

      <div className="px-5 pb-2 label-caps">Workspaces</div>
      <div className="flex-1 overflow-y-auto px-3 space-y-1">
        {projects.map((p) => (
          <Link key={p.id} to={`/app/projects/${p.id}`} onClick={onNavigate}
            data-testid={`nav-project-${p.id}`}
            className={`flex items-center gap-3 px-3 min-h-12 py-2 rounded-md transition-colors ${
              activeId === p.id ? "bg-card text-foreground" : "hover:bg-card/60 text-muted-foreground"
            }`}>
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: PROJECT_STATUS_COLOR[p.status] }} />
            <span className="flex-1 truncate text-sm font-medium">{p.name}</span>
            {activeId === p.id && <ChevronRight className="h-4 w-4 text-primary" />}
          </Link>
        ))}
        {projects.length === 0 && (
          <div className="px-3 py-6 text-sm text-muted-foreground flex flex-col items-center gap-2">
            <Building2 className="h-6 w-6" />
            No projects yet
          </div>
        )}
      </div>

      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-10 w-10 rounded-md bg-secondary grid place-items-center font-data font-bold text-secondary-foreground shrink-0">
            {initials(user?.first_name, user?.last_name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{user?.first_name} {user?.last_name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.system_role || "Member"}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button"
            className="h-10 w-10 text-muted-foreground hover:text-destructive">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-72 shrink-0 border-r border-border h-screen sticky top-0">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72 h-full border-r border-border" >
            <SidebarContent onNavigate={() => setOpen(false)} />
          </div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-40 h-14 flex items-center justify-between px-4 border-b border-border bg-[hsl(var(--sidebar))]">
          <div className="flex items-center gap-2">
            <HardHat className="h-6 w-6 text-primary" />
            <span className="font-display font-black text-sm">CONSTRUCTVISION-3D</span>
          </div>
          <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </header>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
